import {
  CheckCircle as CheckCircleIcon,
  Delete as DeleteIcon,
  TaskAlt as TaskAltIcon,
  RadioButtonUnchecked as UncheckedIcon,
} from "@mui/icons-material";
import { Box, Chip, Divider, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import axios from "axios";
import { API_URL } from "const/constants";
import DisplayDate, { DisplayDateTime } from "components/DisplayDate";
import { formatDisplayDate } from "utils/dateFormat";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import { useCallback, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";

const ClientReminders = () => {
  const { id: clientId } = useParams();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all"); // "all" | "pending" | "completed"

  const { data: reminders = [], isFetching } = useQuery({
    queryKey: ["client-reminders", clientId],
    queryFn: async () => {
      const res = await axios.get(`${API_URL}/client-reminders?clientId=${clientId}`);
      return res?.data?.data ?? [];
    },
    enabled: !!clientId,
    refetchOnWindowFocus: false,
  });

  const refetch = () => queryClient.invalidateQueries(["client-reminders", clientId]);

  const handleToggleComplete = useCallback(async (id, currentlyCompleted) => {
    try {
      await axios.patch(`${API_URL}/client-reminders/${id}/complete`, {
        completed: !currentlyCompleted,
      });
      toast.success(currentlyCompleted ? "Marked as pending" : "Marked as completed");
      refetch();
    } catch {
      toast.error("Failed to update reminder");
    }
  }, []);

  const handleDelete = useCallback(async (id) => {
    if (!window.confirm("Delete this reminder?")) return;
    try {
      setLoading(true);
      await axios.delete(`${API_URL}/client-reminders/${id}`);
      toast.success("Reminder deleted");
      refetch();
    } catch {
      toast.error("Failed to delete reminder");
    } finally {
      setLoading(false);
    }
  }, []);

  const columns = useMemo(
    () => [
      {
        accessorKey: "title",
        header: "Title",
        size: 200,
        Cell: ({ row }) => <div className="whitespace-normal">{row.original.title}</div>,
      },
      {
        accessorKey: "dueDate",
        header: "Due Date",
        size: 130,
        Cell: ({ row }) => <DisplayDate value={row.original.dueDate} />,
      },
      {
        accessorKey: "priority",
        header: "Priority",
        size: 80,
        Cell: ({ row }) => {
          const p = row.original.priority;
          const color =
            p === "High" ? "text-red-500" : p === "Medium" ? "text-yellow-500" : "text-green-500";
          return <span className={color}>{p || "-"}</span>;
        },
      },
      {
        accessorKey: "reason",
        header: "Reason",
        size: 180,
      },
      {
        accessorKey: "action",
        header: "Action",
        size: 110,
        Cell: ({ row }) => (
          <Box className="flex gap-1">
            <Tooltip title={row.original.completed ? "Mark as pending" : "Mark as completed"}>
              <IconButton
                size="small"
                onClick={() => handleToggleComplete(row.original.id, row.original.completed)}
                disabled={loading}
              >
                {row.original.completed ? (
                  <TaskAltIcon color="success" fontSize="small" />
                ) : (
                  <UncheckedIcon color="action" fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton
                size="small"
                onClick={() => handleDelete(row.original.id)}
                disabled={loading}
              >
                <DeleteIcon color="error" fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      },
    ],
    [loading]
  );

  const filteredReminders = useMemo(() => {
    if (statusFilter === "pending") return reminders.filter((r) => !r.completed);
    if (statusFilter === "completed") return reminders.filter((r) => r.completed);
    return reminders;
  }, [reminders, statusFilter]);

  const STATUS_CHIPS = [
    { value: "all", label: "All" },
    { value: "pending", label: "Pending" },
    { value: "completed", label: "Completed" },
  ];

  const table = useMaterialReactTable({
    columns,
    data: filteredReminders,
    initialState: { density: "compact", showGlobalFilter: true },
    state: { isLoading: isFetching },
    renderTopToolbarCustomActions: () => (
      <Stack direction="row" spacing={0.5} sx={{ ml: 1, alignItems: "center" }}>
        {STATUS_CHIPS.map((opt) => (
          <Chip
            key={opt.value}
            label={opt.label}
            size="small"
            clickable
            color={statusFilter === opt.value ? "primary" : "default"}
            variant={statusFilter === opt.value ? "filled" : "outlined"}
            onClick={() => setStatusFilter(opt.value)}
          />
        ))}
      </Stack>
    ),
    enableExpanding: true,
    renderDetailPanel: ({ row }) => {
      const r = row.original;
      return (
        <Box sx={{ p: 2, backgroundColor: "background.default" }}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={3} flexWrap="wrap">
            {r.type && (
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={700}>
                  TYPE
                </Typography>
                <Typography variant="body2">{r.type}</Typography>
              </Box>
            )}
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={700}>
                STATUS
              </Typography>
              <Typography variant="body2">
                {r.status || (r.completed ? "Completed" : "Pending")}
              </Typography>
            </Box>
          </Stack>

          {r.reason && (
            <>
              <Divider sx={{ my: 1.5 }} />
              <Typography variant="caption" color="text.secondary" fontWeight={700}>
                REASON
              </Typography>
              <Typography variant="body2">{r.reason}</Typography>
            </>
          )}

          {r.description && (
            <>
              <Divider sx={{ my: 1.5 }} />
              <Typography variant="caption" color="text.secondary" fontWeight={700}>
                DESCRIPTION
              </Typography>
              <Typography variant="body2">{r.description}</Typography>
            </>
          )}

          {r.reminderDates?.length > 0 && (
            <>
              <Divider sx={{ my: 1.5 }} />
              <Typography variant="caption" color="text.secondary" fontWeight={700}>
                SCHEDULED DATES
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" mt={0.5}>
                {r.reminderDates.map((d) => (
                  <Chip key={d} size="small" label={formatDisplayDate(d)} variant="outlined" />
                ))}
              </Stack>
            </>
          )}

          {r.repeat?.enabled && (
            <>
              <Divider sx={{ my: 1.5 }} />
              <Typography variant="caption" color="text.secondary" fontWeight={700}>
                REPEAT
              </Typography>
              <Typography variant="body2">
                Every {r.repeat.interval} {r.repeat.unit}
              </Typography>
            </>
          )}
        </Box>
      );
    },
    muiTableContainerProps: { sx: { maxHeight: "60vh" } },
    enableStickyHeader: true,
    paginationDisplayMode: "pages",
    muiPaginationProps: { rowsPerPageOptions: [20, 50, 100] },
  });

  return <MaterialReactTable table={table} />;
};

export default ClientReminders;
